import React from 'react';
import ManhuaCard from './ManhuaCard';
import AICuratorPanel from './AICuratorPanel';
import AdSlot from './AdSlot';

const Loader = () => (
  <div className="text-center py-12">
    <div className="inline-flex items-center text-rose-400">
      <svg className="animate-spin -ml-1 mr-3 h-7 w-7" viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <span className="text-lg">Sifting through manhwa, manhua, manga...</span>
    </div>
  </div>
);

const Empty = ({ stats }) => (
  <div className="text-center py-16">
    <div className="text-5xl mb-3">📖</div>
    <h3 className="text-xl font-bold text-zinc-100 mb-2">Discover Webtoons & Manga with AI</h3>
    <p className="text-zinc-400 max-w-xl mx-auto mb-2">
      Describe what you want to read — themes, vibes, tropes — and our semantic engine finds it.
    </p>
    {stats?.total_manhua ? (
      <p className="text-xs text-zinc-500">
        Searching {stats.total_manhua.toLocaleString()} titles · sourced from MangaDex, ratings via AniList
      </p>
    ) : null}
  </div>
);

const NoResults = ({ query }) => (
  <div className="text-center py-12 text-zinc-300">
    <div className="text-4xl mb-3">🔍</div>
    <h3 className="text-lg font-semibold mb-1">Nothing matches "{query}"</h3>
    <p className="text-sm text-zinc-500">Try mentioning a genre, trope, or vibe (e.g. "regression revenge thriller")</p>
  </div>
);

const ManhuaResults = ({ searchResults, isLoading, error, currentQuery, onCardClick, stats }) => {
  if (isLoading) return <Loader />;
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="max-w-md mx-auto bg-red-950/40 border border-red-500/30 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-red-300 mb-2">Search failed</h3>
          <p className="text-sm text-red-200/80">{error}</p>
        </div>
      </div>
    );
  }
  if (!searchResults) return <Empty stats={stats} />;
  if (searchResults.results.length === 0) return <NoResults query={currentQuery} />;

  return (
    <div className="animate-fade-in">
      {/* Interactive AI Curator — big tiles + refine chips + chat */}
      <AICuratorPanel
        query={currentQuery}
        results={searchResults.results}
        kind="manhua"
        onItemClick={onCardClick}
      />

      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between text-zinc-300">
        <div>
          <h2 className="text-xl font-semibold text-zinc-100">Results</h2>
          <p className="text-sm text-zinc-400">
            {searchResults.total_results} matches for <em className="text-rose-300">"{currentQuery}"</em>
          </p>
        </div>
        <div className="text-xs text-zinc-500 mt-2 sm:mt-0">
          {searchResults.search_time_ms}ms
        </div>
      </div>

      {/* Ad: top of results (after the AI rail). Never on the reader route. */}
      <AdSlot id="manhua-results-top" format="leaderboard" />

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {searchResults.results.map((m, i) => (
          <React.Fragment key={m.id}>
            <div
              className="animate-slide-up"
              style={{ animationDelay: `${i * 40}ms` }}
            >
              <ManhuaCard manhua={m} onClick={onCardClick} />
            </div>
            {i === 9 && (
              <div className="col-span-full">
                <AdSlot id="manhua-results-ingrid" format="in-grid" />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default ManhuaResults;
